/**
 * Demand due date → automatic Unpaid status when the deadline passes
 * with no recorded payment (partial or full).
 *
 * Unpaid board column uses Status = LITIGATION_READY.
 */

import {
  isPaidStatus,
  listMatterPaymentEntries,
  matterAmountPaid,
  normalizeMatterStatus,
} from './paidBeforeAssignment.js';

export const UNPAID_STATUS = 'LITIGATION_READY';

/**
 * @param {Date} [now]
 * @returns {string} YYYY-MM-DD in local calendar
 */
export function todayDateString(now = new Date()) {
  const date = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Normalize stored/input due dates to YYYY-MM-DD (or empty).
 * @param {unknown} value
 */
export function normalizeDueDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (!match) return '';
  const iso = match[1];
  const probe = new Date(`${iso}T00:00:00`);
  if (Number.isNaN(probe.getTime())) return '';
  return iso;
}

/**
 * True when the calendar day after Due_Date has started (payment was due by end of due day).
 * @param {unknown} dueDate
 * @param {Date} [now]
 */
export function isDueDatePast(dueDate, now = new Date()) {
  const due = normalizeDueDate(dueDate);
  const today = todayDateString(now);
  if (!due || !today) return false;
  return due < today;
}

/**
 * Any recorded payment (ledger entry or Amount_Paid) blocks auto-unpaid.
 * @param {object} matter
 */
export function matterHasRecordedPayment(matter = {}) {
  if (listMatterPaymentEntries(matter).length > 0) return true;
  return matterAmountPaid(matter) > 0;
}

/**
 * @param {object} matter
 * @param {Date} [now]
 */
export function shouldMarkMatterUnpaidForDueDate(matter, now = new Date()) {
  if (!matter) return false;
  if (isPaidStatus(matter.Status)) return false;
  const status = normalizeMatterStatus(matter.Status);
  if (status === UNPAID_STATUS || status === 'UNPAID_ESCALATE') return false;
  if (!isDueDatePast(matter.Due_Date ?? matter.dueDate, now)) return false;
  if (matterHasRecordedPayment(matter)) return false;
  return true;
}

/**
 * @param {object} matter
 * @param {Date} [now]
 * @returns {{ changed: boolean, matter: object }}
 */
export function applyDueDateUnpaidStatus(matter, now = new Date()) {
  if (!shouldMarkMatterUnpaidForDueDate(matter, now)) {
    return { changed: false, matter };
  }
  const prior = normalizeMatterStatus(matter.Status) || matter.Status;
  const stamped = now instanceof Date ? now : new Date(now);
  return {
    changed: true,
    matter: {
      ...matter,
      Status: UNPAID_STATUS,
      Status_Before_Unpaid: prior,
      Updated_At: Number.isNaN(stamped.getTime())
        ? new Date().toISOString()
        : stamped.toISOString(),
      Resolution_Note:
        matter.Resolution_Note ||
        'Automatically marked unpaid — due date passed with no payment recorded.',
    },
  };
}

/**
 * @param {object[]} matters
 * @param {Date} [now]
 * @returns {{ matters: object[], changed: object[] }}
 */
export function applyDueDateUnpaidToList(matters = [], now = new Date()) {
  const next = [];
  const changed = [];
  for (const matter of matters || []) {
    const result = applyDueDateUnpaidStatus(matter, now);
    next.push(result.matter);
    if (result.changed) changed.push(result.matter);
  }
  return { matters: next, changed };
}
